import type { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';

/**
 * Class for managing the loading and storage of scenes.
 */
export class EngineContext {
  /** Graphics engine. */
  private readonly _engine: Engine;
  /** Scenes. */
  private readonly _scenes = new Map<string, Scene>();

  /**
   * Create a new instance.
   * @param engine - Graphics engine.
   */
  constructor(engine: Engine) {
    this._engine = engine;
  }

  /** Returns the graphics engine. */
  get engine(): Engine {
    return this._engine;
  }

  /**
   * Returns the scene with the given ID.
   * @param sceneId - ID of the scene to retrieve.
   */
  public getScene(sceneId: string): Scene | undefined {
    return this._scenes.get(sceneId);
  }

  /**
   * Releases resources associated with this context.
   */
  public dispose() {
    this._scenes.forEach(scene => {
      scene.dispose();
    });
    this._scenes.clear();
  }

  /**
   * Creates a new managed scene.
   */
  public createScene(): Scene {
    const scene = new Scene(this._engine);
    EngineContext.InitScene(scene);
    this._scenes.set(scene.uid, scene);
    return scene;
  }

  /**
   * Releases resources associated with a scene.
   * @param sceneId - ID of the scene to retrieve.
   */
  public disposeScene(sceneId: string) {
    const scene = this._scenes.get(sceneId);
    if (!scene) {
      return;
    }
    scene.dispose();
    this._scenes.delete(sceneId);
  }

  /**
   * Default configuration for SEDDI scenes.
   * @param babylonScene - Babylon scene.
   */
  public static InitScene(babylonScene: Scene) {
    // SEDDI uses right handed coordinates system
    babylonScene.useRightHandedSystem = true;
  }
}
